import { ICharacter, NotificationType, useApp, useTranslatedTag, useTranslation } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { Chip, ChipVariant, IconVariants } from "@next-platform/client-common-spicy-ui";
import { AnimatePresence, motion, MotionProps } from "framer-motion";
import { MouseEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import useApi from "@/components/useApi";
import { SUGGEST_TAG_EVENT_NAME, TAG_SUGGESTION_KEY } from "@/constants";
import useToast from "@/hooks/useToast";
import { cn } from "@/utils";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import TagSuggestionItem, { TagSuggestionItemCommonProps } from "./TagSuggestionItem";
import TagSuggestionMenu from "./TagSuggestionMenu";
import TagSuggestionMenuOptions from "./TagSuggestionMenuOptions";

const MAX_TAGS_ALLOWED = 12;

const logger = appLogger.withTag("TagSuggestion");

const MOTION_DIV_ANIMATION_CONFIG: MotionProps = {
  initial: { opacity: 0, scale: 0 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0 },
};

const VIEWPORT_OVERFLOW_MARGIN = 16;
const CLIPPING_OVERFLOW_VALUES = new Set(["hidden", "clip", "scroll", "auto"]);

function getMenuMotionClassName(alignment: "start" | "end") {
  return cn("absolute top-10 shadow-large z-10 max-mob:w-full", alignment === "end" ? "right-0 origin-top-right" : "left-0 origin-top-left");
}

// TagSuggestion also renders inside scrollable/clipped containers (the Discover profile
// overlay's dialog panel, for one) that are narrower than the viewport. A chip near their right
// edge can still have room before window.innerWidth while the menu is actually clipped or
// scrolled out of view, so find the nearest ancestor that would clip it and use that as the
// right boundary instead.
function getRightOverflowBoundary(element: HTMLElement): number {
  let node = element.parentElement;
  while (node && node !== document.body) {
    const style = getComputedStyle(node);
    if (CLIPPING_OVERFLOW_VALUES.has(style.overflowX) || CLIPPING_OVERFLOW_VALUES.has(style.overflowY)) {
      return Math.min(node.getBoundingClientRect().right, window.innerWidth);
    }
    node = node.parentElement;
  }
  return window.innerWidth;
}

interface ITagSuggestionProps {
  readonly id: ICharacter["id"];
  readonly tags: readonly string[];
  readonly suggestDisabled?: boolean;
}

function TagSuggestion({ id, tags, suggestDisabled }: ITagSuggestionProps) {
  const { getTranslatedTag } = useTranslatedTag();
  const { t } = useTranslation();
  const { tagOptions } = useApp();
  const { callApi, jsonApiService, isLoading } = useApi();
  const [stateAddedTags, setStateAddedTags] = useState<string[]>([]);
  const [stateRemovedTags, setStateRemovedTags] = useState<string[]>([]);
  const { displayToast } = useToast();
  const suggestedTags = JSON.parse(typedLocalStorageService.getItem(TAG_SUGGESTION_KEY) as string);
  const currentBotSuggestedTags = suggestedTags?.[id];
  const totalTagCount = tags?.length - stateRemovedTags?.length + stateAddedTags?.length;

  const [stateIsTagSuggestionMenu, setStateIsTagSuggestionMenu] = useState(false);
  const [stateMenuAlignment, setStateMenuAlignment] = useState<"start" | "end">("start");
  const tagChipsRef = useRef<HTMLDivElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const refMenuPanel = useRef<HTMLDivElement>(null);

  const handleAddedTags = useCallback(
    (tag: string) => {
      if (suggestDisabled || !stateIsTagSuggestionMenu || isLoading) {
        return;
      }
      setStateAddedTags(stateAddedTags.filter((existingTag) => existingTag !== tag));
    },
    [suggestDisabled, isLoading, stateAddedTags, stateIsTagSuggestionMenu],
  );

  const handleRemovedTags = useCallback(
    (tag: string) => {
      if (suggestDisabled || !stateIsTagSuggestionMenu || isLoading) {
        return;
      }
      if (stateRemovedTags.includes(tag)) {
        setStateRemovedTags(stateRemovedTags.filter((value) => value !== tag));
      } else {
        setStateRemovedTags([...stateRemovedTags, tag]);
      }
    },
    [suggestDisabled, isLoading, stateIsTagSuggestionMenu, stateRemovedTags],
  );

  const handleTagAddition = useCallback(
    (selectedTags: string[]) => {
      if (suggestDisabled || isLoading) {
        return;
      }
      if (totalTagCount >= MAX_TAGS_ALLOWED) {
        displayToast({ message: t("chatbot:tags.error.maxTags", { max: MAX_TAGS_ALLOWED }), type: NotificationType.error });
        return;
      }
      setStateAddedTags(selectedTags);
    },
    [displayToast, suggestDisabled, isLoading, t, totalTagCount],
  );

  const handleMenu = useCallback(() => {
    if (suggestDisabled || isLoading) {
      return;
    }
    setStateIsTagSuggestionMenu((previous) => !previous);
    if (currentBotSuggestedTags) {
      setStateAddedTags(currentBotSuggestedTags.addedTags);
      setStateRemovedTags(currentBotSuggestedTags.removedTags);
    } else {
      setStateAddedTags([]);
      setStateRemovedTags([]);
    }
  }, [currentBotSuggestedTags, suggestDisabled, isLoading]);

  const closeMenu = useCallback(() => {
    setStateIsTagSuggestionMenu(false);

    if (currentBotSuggestedTags) {
      setStateAddedTags(currentBotSuggestedTags.addedTags);
      setStateRemovedTags(currentBotSuggestedTags.removedTags);
    } else {
      setStateAddedTags([]);
      setStateRemovedTags([]);
    }
  }, [currentBotSuggestedTags]);

  const handleSuggestTagMenuToggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (suggestDisabled || isLoading) {
        return;
      }
      handleMenu();
    },
    [handleMenu, isLoading, suggestDisabled],
  );

  const handleSendSuggestion = useCallback(async () => {
    if (suggestDisabled) {
      return;
    }
    if (!stateAddedTags.length && !stateRemovedTags.length) {
      displayToast({ message: t("chatbot:tags.suggestions.pleaseSelect"), type: NotificationType.error });
      return;
    }
    const userAddedTags = stateAddedTags;
    const userRemovedTags = stateRemovedTags;
    setStateIsTagSuggestionMenu(false);
    try {
      await callApi(jsonApiService.createResource, "event", {
        event_name: SUGGEST_TAG_EVENT_NAME,
        character_id: id,
        tags_to_add: userAddedTags,
        tags_to_remove: userRemovedTags,
      });

      displayToast({ message: t("chatbot:tags.suggestions.sentSuccess"), type: NotificationType.success });
      setStateAddedTags(userAddedTags);
      setStateRemovedTags(userRemovedTags);
      typedLocalStorageService.setItem(
        TAG_SUGGESTION_KEY,
        JSON.stringify({
          ...suggestedTags,
          [id]: { addedTags: userAddedTags, removedTags: userRemovedTags },
        }),
      );
    } catch (error) {
      setStateAddedTags(currentBotSuggestedTags?.addedTags || []);
      setStateRemovedTags(currentBotSuggestedTags?.removedTags || []);
      logger.error("error while sending tag suggestion", error);
      displayToast({ message: t("chatbot:tags.error.sendFailed"), type: NotificationType.error });
    }
  }, [
    callApi,
    currentBotSuggestedTags?.addedTags,
    currentBotSuggestedTags?.removedTags,
    displayToast,
    id,
    suggestDisabled,
    jsonApiService,
    stateAddedTags,
    stateRemovedTags,
    suggestedTags,
    t,
  ]);

  const handleDiscard = useCallback(async () => {
    if (suggestDisabled) {
      return;
    }
    setStateIsTagSuggestionMenu(false);
    if (currentBotSuggestedTags) {
      try {
        await callApi(jsonApiService.createResource, "event", {
          event_name: SUGGEST_TAG_EVENT_NAME,
          character_id: id,
          tags_to_add: [],
          tags_to_remove: [],
        });
        displayToast({ message: t("chatbot:tags.suggestions.discardedSuccess"), type: NotificationType.success });
        setStateAddedTags([]);
        setStateRemovedTags([]);
        delete suggestedTags?.[id];
        typedLocalStorageService.setItem(TAG_SUGGESTION_KEY, JSON.stringify(suggestedTags));
      } catch (error) {
        displayToast({ message: t("chatbot:tags.error.discardFailed"), type: NotificationType.error });
        logger.error("error while discarding tag suggestion", error);
      }
    } else {
      setStateAddedTags([]);
      setStateRemovedTags([]);
    }
  }, [callApi, currentBotSuggestedTags, displayToast, id, suggestDisabled, jsonApiService, suggestedTags, t]);

  useEffect(() => {
    if (suggestDisabled) {
      setStateIsTagSuggestionMenu(false);
      setStateAddedTags([]);
      setStateRemovedTags([]);
      return;
    }

    if (currentBotSuggestedTags) {
      const lsAddedTags = currentBotSuggestedTags.addedTags;
      const lsRemovedTags = currentBotSuggestedTags.removedTags;

      const updatedAddedTags = lsAddedTags?.filter((tag: string) => !tags.includes(tag));
      const updatedRemovedTags = lsRemovedTags?.filter((tag: string) => tags.includes(tag));

      typedLocalStorageService.setItem(
        TAG_SUGGESTION_KEY,
        JSON.stringify({
          ...suggestedTags,
          [id]: {
            addedTags: updatedAddedTags,
            removedTags: updatedRemovedTags,
          },
        }),
      );
      setStateAddedTags(updatedAddedTags);
      setStateRemovedTags(updatedRemovedTags);
    } else {
      setStateAddedTags([]);
      setStateRemovedTags([]);
    }
    // Mount-only localStorage sync for a given character (id).
    // `tags` is intentionally omitted so in-progress user edits are not overwritten.
    // `suggestDisabled` is included so that when the control becomes available
    // (e.g. overlay starts disabled then enables), we still hydrate the saved
    // suggestions. While disabled we clear instead of hydrating.
  }, [id, suggestDisabled]);

  useLayoutEffect(() => {
    if (!stateIsTagSuggestionMenu) {
      setStateMenuAlignment("start");
      return;
    }

    const panel = refMenuPanel.current;
    const anchor = menuContainerRef.current;
    if (!panel || !anchor) {
      return;
    }

    // Measure from the anchor's own left edge, not the panel's current rect: the panel's rect
    // reflects whichever alignment is already applied, so once it's "end" its right edge is
    // pinned to the anchor and the check would always read as non-overflowing, masking whether
    // "start" (left-0 from the anchor) would actually still overflow.
    // offsetWidth (layout box) rather than getBoundingClientRect (paint box) for the panel width:
    // framer-motion's enter transform (scale: 0 -> 1) is still applied at this point, which would
    // collapse the measured width to 0 and always report "no overflow".
    const rightBoundary = getRightOverflowBoundary(anchor);
    const isOverflowingRight = anchor.getBoundingClientRect().left + panel.offsetWidth > rightBoundary - VIEWPORT_OVERFLOW_MARGIN;
    setStateMenuAlignment(isOverflowingRight ? "end" : "start");
    // totalTagCount isn't read above, but adding/removing a tag while the menu is open can wrap
    // the trigger chip onto a different line, so re-measure whenever the row's content changes.
  }, [stateIsTagSuggestionMenu, totalTagCount]);

  useEffect(() => {
    if (!stateIsTagSuggestionMenu) {
      return;
    }

    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;

      if (tagChipsRef.current?.contains(target) || menuContainerRef.current?.contains(target)) {
        return;
      }

      closeMenu();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [stateIsTagSuggestionMenu, closeMenu]);

  const handleDiscardPress = useCallback(() => {
    void handleDiscard();
  }, [handleDiscard]);

  const handleSendPress = useCallback(() => {
    void handleSendSuggestion();
  }, [handleSendSuggestion]);

  const getTagAriaLabel = useCallback(
    (tagName: string) => t("chatbot:profile.tag.ariaLabel", { tag: getTranslatedTag(tagName) }),
    [getTranslatedTag, t],
  );

  const tagSuggestionItemCommonProps: TagSuggestionItemCommonProps = useMemo(
    () => ({
      isMenuOpen: stateIsTagSuggestionMenu,
      getTranslatedTag,
      getTagAriaLabel,
    }),
    [getTagAriaLabel, getTranslatedTag, stateIsTagSuggestionMenu],
  );

  return (
    <Row className="flex-row flex-wrap gap-1" data-testid="TagSuggestion">
      <div ref={tagChipsRef} className="contents">
        {tags?.map((tag: string) => (
          <TagSuggestionItem
            key={tag}
            {...tagSuggestionItemCommonProps}
            tag={tag}
            mode="existing"
            isMarkedForRemoval={stateRemovedTags.includes(tag)}
            onChipClick={!suggestDisabled ? handleRemovedTags : undefined}
          />
        ))}
        {stateAddedTags?.map((tag: string) => (
          <TagSuggestionItem
            key={tag}
            {...tagSuggestionItemCommonProps}
            tag={tag}
            mode="added"
            onChipClick={!suggestDisabled ? handleAddedTags : undefined}
          />
        ))}
      </div>
      <div ref={menuContainerRef} className="relative max-mob:w-full">
        {!suggestDisabled && (
          <Chip
            data-testid="TagSuggestion-SuggestButton"
            variant={ChipVariant.OUTLINED}
            onClick={handleSuggestTagMenuToggle}
            iconStart={stateIsTagSuggestionMenu ? IconVariants.CLOSE : IconVariants.PLUS}
            loading={isLoading}
            aria-label={t("chatbot:tags.suggestTag.ariaLabel")}
          >
            <Trans i18nKey="chatbot:tags.suggestTag" />
          </Chip>
        )}

        <AnimatePresence>
          {!suggestDisabled && stateIsTagSuggestionMenu ? (
            <motion.div ref={refMenuPanel} {...MOTION_DIV_ANIMATION_CONFIG} className={getMenuMotionClassName(stateMenuAlignment)}>
              <TagSuggestionMenu
                bottomContent={<TagSuggestionMenuOptions onDiscardPress={handleDiscardPress} onSendPress={handleSendPress} />}
                tagOptions={tagOptions}
                addedTags={stateAddedTags}
                handleTagAddition={handleTagAddition}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </Row>
  );
}

export default TagSuggestion;
